import React, { useCallback, useEffect, useState } from "react";
import { Play, Check, RotateCcw } from "lucide-react";
import { Spinner } from "./ui/kit";
import OsobaKolecko from "./OsobaKolecko";
import { ikonaTypu } from "../lib/typyAktivit";
import { sklon } from "../lib/filtr";
import { kdyKrok } from "./ObchodTabule";
import { useObnovaPriNavratu } from "../lib/obnova";
import api, { apiError } from "../lib/api";

// Úkol, hovor a e-mail mají termín na začátku, události s délkou na konci (backend termin_aktivity.py).
const UKOLOVE = ["Úkol", "Hovor", "E-mail"];
const terminAktivity = (a) => (UKOLOVE.includes(a.typ) ? a.date_start || a.date_end : a.date_end || a.date_start);

/**
 * Úkoly jako Kanban: K řešení → Rozpracované → Hotovo. Jen úkoly, hovory a
 * e-maily (věci k odškrtnutí) — schůzky a montáže jsou v kalendáři. Na počítači
 * se karta přetáhne do jiného sloupce, na telefonu (a dotykem) tlačítkem na kartě.
 * Přesun do Hotovo úkol odškrtne, zpět z Hotovo ho znovu otevře.
 */
export const SLOUPCE = [
  { klic: "K_RESENI", data: "k_reseni", nazev: "K řešení", barva: "#2c6ecb" },
  { klic: "ROZPRACOVANO", data: "rozpracovano", nazev: "Rozpracované", barva: "#ff6a13" },
  { klic: "HOTOVO", data: "hotovo", nazev: "Hotovo", barva: "#17876a" },
];
const HOTOVO_PO = 30;

const den = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dniPoTerminu = (iso) => Math.max(1, Math.round((den(new Date()) - den(new Date(iso))) / 86400000));

export function useKanban({ oblast, osoby }) {
  const [d, setD] = useState(null);
  const [limit, setLimit] = useState(HOTOVO_PO);
  const [chyba, setChyba] = useState("");
  const load = useCallback(async () => {
    const q = new URLSearchParams({ hotovo_limit: String(limit) });
    if (oblast) q.set("oblast", oblast);
    (osoby || []).forEach((o) => q.append("osoba", o));
    try { setD((await api.get(`/api/aktivity/kanban?${q}`)).data); setChyba(""); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
  }, [oblast, osoby, limit]);
  useEffect(() => { load(); }, [load]);
  useObnovaPriNavratu(load);

  // Přesun hned na obrazovce, server potvrdí; při chybě se načte znovu.
  const presun = async (a, klic) => {
    const cil = SLOUPCE.find((s) => s.klic === klic);
    setD((x) => {
      if (!x) return x;
      const bez = Object.fromEntries(SLOUPCE.map((s) => [s.data, x[s.data].filter((y) => y.id !== a.id)]));
      const novy = { ...a, stav: klic === "HOTOVO" ? "HOTOVA" : "OTEVRENA", rozpracovano: klic === "ROZPRACOVANO", po_terminu: klic === "HOTOVO" ? false : a.po_terminu };
      bez[cil.data] = klic === "HOTOVO" ? [novy, ...bez[cil.data]]
        : [...bez[cil.data], novy].sort((p, q) => new Date(p.date_start) - new Date(q.date_start));
      return { ...x, ...bez };
    });
    try { await api.post(`/api/aktivity/${a.id}/sloupec`, { sloupec: klic }); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
    load();
  };
  return { d, load, presun, chyba, vic: () => setLimit((l) => l + HOTOVO_PO) };
}

function sloupecKarty(a) {
  if (a.stav === "HOTOVA") return "HOTOVO";
  return a.rozpracovano ? "ROZPRACOVANO" : "K_RESENI";
}

/** Tlačítka posunu na kartě — jediný způsob na telefonu, na počítači doplněk k tažení. */
function Posun({ a, onPresun, vzdy }) {
  const s = sloupecKarty(a);
  const akce = s === "K_RESENI" ? [["ROZPRACOVANO", "Začít", Play], ["HOTOVO", "Hotovo", Check]]
    : s === "ROZPRACOVANO" ? [["HOTOVO", "Hotovo", Check], ["K_RESENI", "Zpět", RotateCcw]]
    : [["K_RESENI", "Otevřít znovu", RotateCcw]];
  return (
    <span className={`gap-1.5 ${vzdy ? "flex" : "flex md:hidden md:group-hover:flex md:group-focus-within:flex [@media(pointer:coarse)]:flex"}`}>
      {akce.map(([klic, text, Ikona]) => (
        <button key={klic} type="button" onClick={(e) => { e.stopPropagation(); onPresun(a, klic); }}
          data-testid={`kanban-${klic.toLowerCase()}-${a.id}`}
          className="tap md:min-h-0 md:min-w-0 h-9 md:h-7 px-3 md:px-2.5 rounded-pill border border-line-strong bg-white t-meta font-600 text-sec hover:text-ink inline-flex items-center gap-1">
          <Ikona size={13} />{text}
        </button>
      ))}
    </span>
  );
}

function Karta({ a, onOtevri, onPresun, tazeni, setTazeni, mobil }) {
  const Ikona = ikonaTypu(a.typ);
  const hotova = a.stav === "HOTOVA";
  const smi = a.muze_editovat !== false;
  const kde = [a.client_name, a.obchodni_pripad?.nazev].filter(Boolean).join(" · ");
  return (
    <article tabIndex={0} data-testid={`kanban-karta-${a.id}`} draggable={smi && !mobil}
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", a.id); e.dataTransfer.effectAllowed = "move"; setTazeni(a.id); }}
      onDragEnd={() => setTazeni(null)}
      onClick={() => onOtevri(a.id)} onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) onOtevri(a.id); }}
      style={{ boxShadow: a.po_terminu ? "inset 3px 0 0 #d93a2b, 0 1px 2px rgba(20,24,33,.04)" : undefined }}
      className={`group rounded-[12px] bg-white border border-line shadow-card px-3 py-2.5 flex flex-col gap-1.5 cursor-pointer select-none hover:shadow-[0_6px_18px_rgba(20,24,33,.1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${
        tazeni === a.id ? "opacity-40" : ""}`}>
      <div className="flex items-start gap-2">
        <Ikona size={15} className="text-muted shrink-0 mt-0.5" aria-label={a.typ} />
        <span className={`flex-1 min-w-0 t-telo font-700 line-clamp-2 ${hotova ? "text-muted line-through" : "text-ink"}`}>{a.nazev}</span>
      </div>
      {kde && <span className="t-meta text-sec truncate">{kde}</span>}
      <div className="flex items-center gap-2 min-h-[22px]">
        <span className={`t-meta amount whitespace-nowrap ${a.po_terminu ? "text-danger font-700" : "text-muted"}`}>
          {hotova ? "hotovo" : a.po_terminu
            ? `${dniPoTerminu(terminAktivity(a))} ${sklon(dniPoTerminu(terminAktivity(a)), ["den", "dny", "dní"])} po termínu`
            : kdyKrok(a.date_start)}
        </span>
        <span className="ml-auto flex items-center">
          {(a.osoby || []).slice(0, 3).map((o, i) => (
            <OsobaKolecko key={o.id} id={o.id} name={o.name} url={o.photo_url} size={22} className={`ring-2 ring-white ${i ? "-ml-1.5" : ""}`} />
          ))}
        </span>
      </div>
      {smi && <Posun a={a} onPresun={onPresun} vzdy={mobil} />}
    </article>
  );
}

/** Počítač a tablet: tři sloupce vedle sebe, karta se přetáhne myší. */
export function KanbanSloupce({ k, onOtevri }) {
  const { d, presun, chyba, vic } = k;
  const [tazeni, setTazeni] = useState(null);
  const [nad, setNad] = useState(null);
  if (!d) return <div className="card p-10 flex justify-center text-muted"><Spinner /></div>;
  const vsechny = SLOUPCE.flatMap((s) => d[s.data]);
  const pust = (klic) => (e) => {
    e.preventDefault(); setNad(null);
    const a = vsechny.find((x) => x.id === e.dataTransfer.getData("text/plain"));
    setTazeni(null);
    if (a && sloupecKarty(a) !== klic) presun(a, klic);
  };
  return (
    <div data-testid="ukoly-kanban">
      {chyba && <div className="card px-4 py-3 mb-3 t-meta text-danger">{chyba}</div>}
      <div className="grid grid-cols-3 gap-[10px] items-start">
        {SLOUPCE.map((s) => {
          const karty = d[s.data];
          return (
            <section key={s.klic} aria-label={s.nazev} data-testid={`kanban-sloupec-${s.klic}`}
              onDragOver={(e) => { e.preventDefault(); setNad(s.klic); }} onDragLeave={() => setNad(null)} onDrop={pust(s.klic)}
              className={`bg-inputbg border rounded-card p-[7px] flex flex-col min-h-[320px] transition-colors ${
                nad === s.klic && tazeni ? "border-accent bg-accent/[0.04]" : "border-line"}`}>
              <div className="rounded-[11px] bg-white px-[11px] py-2 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: s.barva }} />
                <span className="t-sekce font-700 text-ink">{s.nazev}</span>
                <span className="amount t-meta text-muted">{s.klic === "HOTOVO" ? d.hotovo_celkem : karty.length}</span>
                {s.klic !== "HOTOVO" && karty.some((a) => a.po_terminu) && (
                  <span className="ml-auto t-meta text-danger font-600">{karty.filter((a) => a.po_terminu).length} po termínu</span>
                )}
              </div>
              <div className="flex flex-col gap-2 pt-2">
                {karty.map((a) => <Karta key={a.id} a={a} onOtevri={onOtevri} onPresun={presun} tazeni={tazeni} setTazeni={setTazeni} />)}
                {!karty.length && <div className="t-meta text-muted text-center py-6">{s.klic === "ROZPRACOVANO" ? "Přetáhni sem, na čem pracuješ" : "Tady nic není"}</div>}
                {s.klic === "HOTOVO" && d.hotovo_celkem > karty.length && (
                  <button type="button" onClick={vic} className="tap md:min-h-0 h-9 rounded-[10px] t-meta font-700 text-sec hover:text-ink hover:bg-white"
                    data-testid="kanban-starsi">Zobrazit starší ({d.hotovo_celkem - karty.length})</button>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** Telefon: sloupce jako přepínač nahoře, pod ním karty jednoho sloupce s tlačítky posunu. */
export function KanbanMobil({ k, sloupec, setSloupec, onOtevri }) {
  const { d, presun, chyba, vic } = k;
  if (!d) return <div className="card p-10 flex justify-center text-muted"><Spinner /></div>;
  const akt = SLOUPCE.find((s) => s.klic === sloupec) || SLOUPCE[0];
  const karty = d[akt.data];
  return (
    <div data-testid="ukoly-kanban-mobil" className="flex flex-col gap-2.5">
      <div className="grid grid-cols-3 gap-1 p-1 rounded-pill bg-white border border-line-strong" role="tablist">
        {SLOUPCE.map((s) => (
          <button key={s.klic} type="button" role="tab" aria-selected={s.klic === akt.klic} onClick={() => setSloupec(s.klic)}
            data-testid={`kanban-tab-${s.klic}`}
            className={`h-11 rounded-pill t-meta font-700 inline-flex items-center justify-center gap-1 ${s.klic === akt.klic ? "bg-ink text-white" : "text-sec"}`}>
            {s.nazev}<span className={`amount ${s.klic === akt.klic ? "text-white/70" : "text-muted"}`}>{s.klic === "HOTOVO" ? d.hotovo_celkem : d[s.data].length}</span>
          </button>
        ))}
      </div>
      {chyba && <div className="t-meta text-danger">{chyba}</div>}
      {karty.map((a) => <Karta key={a.id} a={a} onOtevri={onOtevri} onPresun={presun} setTazeni={() => {}} mobil />)}
      {!karty.length && <div className="card t-meta text-muted text-center py-8">{akt.klic === "HOTOVO" ? "Zatím nic hotového." : "Tady nic není."}</div>}
      {akt.klic === "HOTOVO" && d.hotovo_celkem > karty.length && (
        <button type="button" onClick={vic} className="tap h-11 rounded-pill border border-line-strong bg-white t-telo font-600 text-sec">
          Zobrazit starší ({d.hotovo_celkem - karty.length})
        </button>
      )}
    </div>
  );
}
